import type { Table } from 'dexie';
import type { ZodType } from 'zod';
import { calculateCalibrationAverage, calculateCalibrationRatio } from '../domain/calculations';
import {
  appSettingsSchema,
  backupDataSchema,
  calibrationMeasurementSchema,
  containerSchema,
  conversionConfigurationSchema,
  dosageTableSchema,
  measuringCupSchema,
  productSchema,
  recommendationRuleSchema,
  stainGuideSchema,
  temperatureGuidanceSchema,
  validateBackupRelations,
  waterHardnessSchema,
} from '../domain/schemas';
import type {
  AppSettings,
  BackupData,
  CalibrationMeasurement,
  Container,
  ConversionConfiguration,
  DosageTable,
  MeasuringCup,
  Product,
  RecommendationRule,
  StainGuide,
  TemperatureGuidance,
  WaterHardness,
} from '../domain/types';
import { db, initializeDatabase, populateDefaultData, type WaschPilotDatabase } from './database';
import {
  SEED_CONTAINERS,
  SEED_CONVERSION_CONFIGURATIONS,
  SEED_DOSAGE_TABLES,
  SEED_MEASURING_CUPS,
  SEED_PRODUCTS,
  SEED_RECOMMENDATION_RULES,
  SEED_SETTINGS,
  SEED_STAIN_GUIDES,
  SEED_TEMPERATURE_GUIDANCE,
  SEED_WATER_HARDNESS,
} from './seed';

export class DataValidationError extends Error {
  constructor(
    message: string,
    public readonly issues: readonly unknown[] = [],
  ) {
    super(message);
    this.name = 'DataValidationError';
  }
}

export class ValidatedRepository<T, TKey> {
  constructor(
    private readonly database: WaschPilotDatabase,
    private readonly table: Table<T, TKey>,
    private readonly schema: ZodType<T>,
    private readonly sort?: (left: T, right: T) => number,
  ) {}

  async list(): Promise<T[]> {
    await initializeDatabase(this.database);
    const values = await this.table.toArray();
    return this.sort ? [...values].sort(this.sort) : values;
  }

  async get(key: TKey): Promise<T | undefined> {
    await initializeDatabase(this.database);
    return this.table.get(key);
  }

  async put(value: T): Promise<TKey> {
    const parsed = this.schema.parse(value);
    await initializeDatabase(this.database);
    return this.database.transaction('rw', this.table, () => this.table.put(parsed));
  }

  async bulkPut(values: readonly T[]): Promise<void> {
    const parsed = values.map((value) => this.schema.parse(value));
    await initializeDatabase(this.database);
    await this.database.transaction('rw', this.table, () => this.table.bulkPut(parsed));
  }

  async delete(key: TKey): Promise<void> {
    await initializeDatabase(this.database);
    await this.database.transaction('rw', this.table, () => this.table.delete(key));
  }
}

const bySortOrder = <T extends { sortOrder: number }>(left: T, right: T) =>
  left.sortOrder - right.sortOrder;

export function createRepositories(database: WaschPilotDatabase = db) {
  return {
    settings: new ValidatedRepository<AppSettings, 'app'>(
      database,
      database.settings,
      appSettingsSchema,
    ),
    products: new ValidatedRepository<Product, string>(
      database,
      database.products,
      productSchema,
      bySortOrder,
    ),
    dosageTables: new ValidatedRepository<DosageTable, string>(
      database,
      database.dosageTables,
      dosageTableSchema,
    ),
    waterHardness: new ValidatedRepository<WaterHardness, string>(
      database,
      database.waterHardness,
      waterHardnessSchema,
      bySortOrder,
    ),
    containers: new ValidatedRepository<Container, string>(
      database,
      database.containers,
      containerSchema,
      bySortOrder,
    ),
    measuringCups: new ValidatedRepository<MeasuringCup, string>(
      database,
      database.measuringCups,
      measuringCupSchema,
      bySortOrder,
    ),
    conversions: new ValidatedRepository<ConversionConfiguration, string>(
      database,
      database.conversionConfigurations,
      conversionConfigurationSchema,
    ),
    calibrationMeasurements: new ValidatedRepository<CalibrationMeasurement, string>(
      database,
      database.calibrationMeasurements,
      calibrationMeasurementSchema,
    ),
    temperatureGuidance: new ValidatedRepository<TemperatureGuidance, string>(
      database,
      database.temperatureGuidance,
      temperatureGuidanceSchema,
      bySortOrder,
    ),
    recommendationRules: new ValidatedRepository<RecommendationRule, string>(
      database,
      database.recommendationRules,
      recommendationRuleSchema,
      (left, right) => right.priority - left.priority,
    ),
    stainGuides: new ValidatedRepository<StainGuide, string>(
      database,
      database.stainGuides,
      stainGuideSchema,
      bySortOrder,
    ),
  };
}

export const repositories = createRepositories();

function newId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function allSchoolTables(database: WaschPilotDatabase) {
  return [
    database.settings,
    database.products,
    database.dosageTables,
    database.waterHardness,
    database.containers,
    database.measuringCups,
    database.conversionConfigurations,
    database.calibrationMeasurements,
    database.temperatureGuidance,
    database.recommendationRules,
    database.stainGuides,
  ] as const;
}

export async function loadAllSchoolData(database: WaschPilotDatabase): Promise<BackupData> {
  await initializeDatabase(database);
  const [
    settings,
    products,
    dosageTables,
    waterHardness,
    containers,
    measuringCups,
    conversionConfigurations,
    calibrationMeasurements,
    temperatureGuidance,
    recommendationRules,
    stainGuides,
  ] = await Promise.all([
    database.settings.toArray(),
    database.products.toArray(),
    database.dosageTables.toArray(),
    database.waterHardness.toArray(),
    database.containers.toArray(),
    database.measuringCups.toArray(),
    database.conversionConfigurations.toArray(),
    database.calibrationMeasurements.toArray(),
    database.temperatureGuidance.toArray(),
    database.recommendationRules.toArray(),
    database.stainGuides.toArray(),
  ]);

  return {
    settings,
    products,
    dosageTables,
    waterHardness,
    containers,
    measuringCups,
    conversionConfigurations,
    calibrationMeasurements,
    temperatureGuidance,
    recommendationRules,
    stainGuides,
  };
}

export async function replaceAllSchoolData(
  database: WaschPilotDatabase,
  input: BackupData,
): Promise<void> {
  const data = backupDataSchema.parse(input);
  const relationIssues = validateBackupRelations(data);
  if (relationIssues.length > 0) {
    throw new DataValidationError('The data contains invalid relationships.', relationIssues);
  }

  await initializeDatabase(database);
  await database.transaction('rw', allSchoolTables(database), async () => {
    await Promise.all(allSchoolTables(database).map((table) => table.clear()));
    await database.settings.bulkPut(data.settings);
    await database.products.bulkPut(data.products);
    await database.dosageTables.bulkPut(data.dosageTables);
    await database.waterHardness.bulkPut(data.waterHardness);
    await database.containers.bulkPut(data.containers);
    await database.measuringCups.bulkPut(data.measuringCups);
    await database.conversionConfigurations.bulkPut(data.conversionConfigurations);
    await database.calibrationMeasurements.bulkPut(data.calibrationMeasurements);
    await database.temperatureGuidance.bulkPut(data.temperatureGuidance);
    await database.recommendationRules.bulkPut(data.recommendationRules);
    await database.stainGuides.bulkPut(data.stainGuides);
  });
}

export type ResetSection =
  | 'products'
  | 'productsAndDosages'
  | 'equipment'
  | 'waterHardness'
  | 'guidance'
  | 'calibrations'
  | 'calibration'
  | 'stains'
  | 'allSchoolData';

export function createTeacherDataService(database: WaschPilotDatabase = db) {
  return {
    loadAll: () => loadAllSchoolData(database),
    saveAll: (data: BackupData) => replaceAllSchoolData(database, data),

    async updateSettings(
      update: Partial<Omit<AppSettings, 'id' | 'createdAt'>>,
    ): Promise<AppSettings> {
      await initializeDatabase(database);
      return database.transaction('rw', database.settings, async () => {
        const existing = await database.settings.get('app');
        if (!existing) throw new DataValidationError('Application settings are missing.');
        const next = appSettingsSchema.parse({
          ...existing,
          ...update,
          id: 'app',
          createdAt: existing.createdAt,
          updatedAt: new Date().toISOString(),
        });
        await database.settings.put(next);
        return next;
      });
    },

    async updateDevicePreferences(
      update: Partial<Pick<AppSettings, 'language' | 'theme' | 'welcomeDismissed'>>,
    ): Promise<AppSettings> {
      await initializeDatabase(database);
      return database.transaction('rw', database.settings, async () => {
        const existing = await database.settings.get('app');
        if (!existing) throw new DataValidationError('Application settings are missing.');
        const next = appSettingsSchema.parse({
          ...existing,
          ...update,
          id: 'app',
          createdAt: existing.createdAt,
          // Per-device choices must not make the shared school record look modified.
          updatedAt: existing.updatedAt,
        });
        await database.settings.put(next);
        return next;
      });
    },

    async duplicateProduct(productId: string): Promise<Product> {
      await initializeDatabase(database);
      return database.transaction(
        'rw',
        [
          database.products,
          database.dosageTables,
          database.conversionConfigurations,
          database.temperatureGuidance,
        ],
        async () => {
          const source = await database.products.get(productId);
          if (!source) throw new DataValidationError('The source product does not exist.');
          const now = new Date().toISOString();
          const copyId = newId('product');
          const copy = productSchema.parse({
            ...structuredClone(source),
            id: copyId,
            name: {
              de: `${source.name.de} (Kopie)`,
              ar: source.name.ar ? `${source.name.ar} (نسخة)` : undefined,
            },
            active: false,
            sortOrder: (await database.products.count()) + 1,
            createdAt: now,
            updatedAt: now,
          });
          const dosageCopies = (
            await database.dosageTables.where('productId').equals(productId).toArray()
          ).map((entry) =>
            dosageTableSchema.parse({ ...entry, id: newId('dosage'), productId: copyId }),
          );
          const temperatureCopies = (
            await database.temperatureGuidance.where('productId').equals(productId).toArray()
          ).map((entry) =>
            temperatureGuidanceSchema.parse({
              ...entry,
              id: newId('temperature'),
              productId: copyId,
            }),
          );
          const sourceConversion = await database.conversionConfigurations.get(productId);
          const conversion = conversionConfigurationSchema.parse({
            productId: copyId,
            schoolGramsPerMl: sourceConversion?.schoolGramsPerMl ?? 1,
            calibratedGramsPerMl: null,
            activeConversionMode: 'school',
            calibrationStatus: 'notCalibrated',
            calibrationDate: null,
            calibrationNote: '',
            updatedAt: now,
          });

          await database.products.add(copy);
          await database.dosageTables.bulkAdd(dosageCopies);
          await database.temperatureGuidance.bulkAdd(temperatureCopies);
          await database.conversionConfigurations.add(conversion);
          return copy;
        },
      );
    },

    async deleteProduct(productId: string): Promise<void> {
      await initializeDatabase(database);
      await database.transaction(
        'rw',
        [
          database.products,
          database.dosageTables,
          database.conversionConfigurations,
          database.calibrationMeasurements,
          database.temperatureGuidance,
          database.recommendationRules,
          database.stainGuides,
        ],
        async () => {
          await database.products.delete(productId);
          await database.dosageTables.where('productId').equals(productId).delete();
          await database.conversionConfigurations.delete(productId);
          await database.calibrationMeasurements.where('productId').equals(productId).delete();
          await database.temperatureGuidance.where('productId').equals(productId).delete();
          await database.recommendationRules.where('productId').equals(productId).delete();
          const affectedStains = await database.stainGuides
            .filter((guide) => guide.recommendedProductId === productId)
            .toArray();
          await database.stainGuides.bulkPut(
            affectedStains.map((guide) => ({
              ...guide,
              recommendedProductId: null,
              updatedAt: new Date().toISOString(),
            })),
          );
        },
      );
    },

    async createCalibrationMeasurement(
      input: Omit<CalibrationMeasurement, 'id' | 'gramsPerMl' | 'measuredAt'>,
    ): Promise<CalibrationMeasurement> {
      const measurement = calibrationMeasurementSchema.parse({
        ...input,
        id: newId('measurement'),
        gramsPerMl: calculateCalibrationRatio(input.measuredMilliliters, input.measuredGrams),
        measuredAt: new Date().toISOString(),
      });
      await initializeDatabase(database);
      await database.calibrationMeasurements.add(measurement);
      return measurement;
    },

    async applyCalibration(productId: string, runId: string, note: string): Promise<number> {
      await initializeDatabase(database);
      return database.transaction(
        'rw',
        [database.calibrationMeasurements, database.conversionConfigurations],
        async () => {
          const measurements = await database.calibrationMeasurements
            .where('runId')
            .equals(runId)
            .filter((measurement) => measurement.productId === productId)
            .toArray();
          const average = calculateCalibrationAverage(measurements);
          const existing = await database.conversionConfigurations.get(productId);
          if (!existing) throw new DataValidationError('The product conversion is missing.');
          const now = new Date().toISOString();
          await database.conversionConfigurations.put(
            conversionConfigurationSchema.parse({
              ...existing,
              calibratedGramsPerMl: average,
              activeConversionMode: 'calibrated',
              calibrationStatus: 'calibrated',
              calibrationDate: now,
              calibrationNote: note.trim(),
              updatedAt: now,
            }),
          );
          return average;
        },
      );
    },

    async setManualConversion(productId: string, gramsPerMl: number, note: string): Promise<void> {
      await initializeDatabase(database);
      await database.transaction('rw', database.conversionConfigurations, async () => {
        const existing = await database.conversionConfigurations.get(productId);
        if (!existing) throw new DataValidationError('The product conversion is missing.');
        const now = new Date().toISOString();
        await database.conversionConfigurations.put(
          conversionConfigurationSchema.parse({
            ...existing,
            calibratedGramsPerMl: gramsPerMl,
            activeConversionMode: 'calibrated',
            calibrationStatus: 'manual',
            calibrationDate: now,
            calibrationNote: note,
            updatedAt: now,
          }),
        );
      });
    },

    async useSchoolConversion(productId: string): Promise<void> {
      await initializeDatabase(database);
      await database.transaction('rw', database.conversionConfigurations, async () => {
        const existing = await database.conversionConfigurations.get(productId);
        if (!existing) throw new DataValidationError('The product conversion is missing.');
        await database.conversionConfigurations.put(
          conversionConfigurationSchema.parse({
            ...existing,
            activeConversionMode: 'school',
            updatedAt: new Date().toISOString(),
          }),
        );
      });
    },

    async resetSection(section: ResetSection): Promise<void> {
      await initializeDatabase(database);
      if (section === 'allSchoolData') {
        await database.transaction('rw', allSchoolTables(database), async () => {
          await Promise.all(allSchoolTables(database).map((table) => table.clear()));
          await populateDefaultData(database);
        });
        return;
      }

      if (section === 'calibrations' || section === 'calibration') {
        await database.transaction(
          'rw',
          [database.products, database.calibrationMeasurements, database.conversionConfigurations],
          async () => {
            await database.calibrationMeasurements.clear();
            const products = await database.products.toArray();
            await database.conversionConfigurations.clear();
            await database.conversionConfigurations.bulkPut(
              products.map((product) => ({
                productId: product.id,
                schoolGramsPerMl: 1,
                calibratedGramsPerMl: null,
                activeConversionMode: 'school',
                calibrationStatus: 'notCalibrated',
                calibrationDate: null,
                calibrationNote: '',
                updatedAt: new Date().toISOString(),
              })),
            );
          },
        );
        return;
      }

      if (section === 'stains') {
        await database.transaction('rw', database.stainGuides, async () => {
          await database.stainGuides.clear();
          await database.stainGuides.bulkPut(structuredClone([...SEED_STAIN_GUIDES]));
        });
        return;
      }

      if (section === 'equipment') {
        await database.transaction(
          'rw',
          [database.containers, database.measuringCups, database.calibrationMeasurements],
          async () => {
            await database.containers.clear();
            await database.measuringCups.clear();
            await database.containers.bulkPut(structuredClone([...SEED_CONTAINERS]));
            await database.measuringCups.bulkPut(structuredClone([...SEED_MEASURING_CUPS]));
            const measurements = await database.calibrationMeasurements.toArray();
            await database.calibrationMeasurements.bulkPut(
              measurements.map((measurement) => ({ ...measurement, cupId: null })),
            );
          },
        );
        return;
      }

      if (section === 'waterHardness') {
        await database.transaction(
          'rw',
          [database.products, database.waterHardness, database.settings, database.dosageTables],
          async () => {
            await database.waterHardness.clear();
            await database.waterHardness.bulkPut(structuredClone([...SEED_WATER_HARDNESS]));
            const settings = await database.settings.get('app');
            if (settings) {
              await database.settings.put({
                ...settings,
                defaultWaterHardnessId: SEED_SETTINGS.defaultWaterHardnessId,
                waterHardnessLocked: SEED_SETTINGS.waterHardnessLocked,
                updatedAt: new Date().toISOString(),
              });
            }
            const defaultProductIds = new Set(SEED_PRODUCTS.map(({ id }) => id));
            const existingProductIds = new Set(
              (await database.products.toArray()).map(({ id }) => id),
            );
            const customEntries = await database.dosageTables
              .filter((entry) => !defaultProductIds.has(entry.productId))
              .toArray();
            await database.dosageTables.clear();
            await database.dosageTables.bulkPut([
              ...structuredClone(
                SEED_DOSAGE_TABLES.filter((entry) => existingProductIds.has(entry.productId)),
              ),
              ...customEntries.filter((entry) =>
                SEED_WATER_HARDNESS.some(({ id }) => id === entry.waterHardnessId),
              ),
            ]);
          },
        );
        return;
      }

      if (section === 'guidance') {
        await database.transaction(
          'rw',
          [database.products, database.temperatureGuidance, database.recommendationRules],
          async () => {
            const productIds = new Set((await database.products.toArray()).map(({ id }) => id));
            await database.temperatureGuidance.clear();
            await database.recommendationRules.clear();
            await database.temperatureGuidance.bulkPut(
              structuredClone(
                SEED_TEMPERATURE_GUIDANCE.filter((entry) => productIds.has(entry.productId)),
              ),
            );
            await database.recommendationRules.bulkPut(
              structuredClone(
                SEED_RECOMMENDATION_RULES.filter(
                  (entry) => entry.productId === null || productIds.has(entry.productId),
                ),
              ),
            );
          },
        );
        return;
      }

      await database.transaction(
        'rw',
        [
          database.products,
          database.dosageTables,
          database.conversionConfigurations,
          database.calibrationMeasurements,
          database.temperatureGuidance,
          database.recommendationRules,
          database.stainGuides,
        ],
        async () => {
          await Promise.all([
            database.products.clear(),
            database.dosageTables.clear(),
            database.conversionConfigurations.clear(),
            database.calibrationMeasurements.clear(),
            database.temperatureGuidance.clear(),
            database.recommendationRules.clear(),
          ]);
          const affectedStains = await database.stainGuides
            .filter((guide) => guide.recommendedProductId !== null)
            .toArray();
          await database.stainGuides.bulkPut(
            affectedStains.map((guide) => ({ ...guide, recommendedProductId: null })),
          );
          await database.products.bulkPut(structuredClone([...SEED_PRODUCTS]));
          await database.dosageTables.bulkPut(structuredClone([...SEED_DOSAGE_TABLES]));
          await database.conversionConfigurations.bulkPut(
            structuredClone([...SEED_CONVERSION_CONFIGURATIONS]),
          );
          await database.temperatureGuidance.bulkPut(
            structuredClone([...SEED_TEMPERATURE_GUIDANCE]),
          );
          await database.recommendationRules.bulkPut(
            structuredClone([...SEED_RECOMMENDATION_RULES]),
          );
        },
      );
    },

    async factoryReset(): Promise<void> {
      database.close();
      await database.delete();
      await database.open();
    },
  };
}

export const teacherDataService = createTeacherDataService();
